import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { colors } from "../theme/colors";
import { createCommonStyles } from "../theme/styles";
import { darkColors, lightColors } from "../theme";

const AppointmentViewDialog = ({
  open,
  onClose,
  appointment,
  services = [],
  tenants = [],
  userType,
  isDarkMode,
}) => {
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);

  if (!appointment) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          background: isDarkMode
            ? "rgba(26, 26, 46, 0.95)"
            : "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          border: isDarkMode
            ? "1px solid rgba(148, 163, 184, 0.1)"
            : "1px solid rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          ...commonStyles.textPrimary,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 2,
          borderBottom: `1px solid ${customColors.border.light}`,
        }}
      >
        <Typography variant="h6" sx={commonStyles.textPrimary}>
          Pregled termina
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: commonStyles.textSecondary.color,
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(148, 163, 184, 0.1)"
                : "rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Status i Datum */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "200px" }}>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Status
              </Typography>
              <Chip
                label={
                  appointment.status === "pending"
                    ? "Na čekanju"
                    : appointment.status === "confirmed"
                    ? "Potvrđeno"
                    : appointment.status === "cancelled"
                    ? "Otkazano"
                    : "Završeno"
                }
                size="small"
                sx={{
                  backgroundColor:
                    appointment.status === "pending"
                      ? colors.status.appointment.background.pending
                      : appointment.status === "confirmed"
                      ? colors.status.appointment.background.confirmed
                      : appointment.status === "cancelled"
                      ? colors.status.appointment.background.cancelled
                      : colors.status.appointment.background.completed,
                  color:
                    appointment.status === "pending"
                      ? colors.status.appointment.pending
                      : appointment.status === "confirmed"
                      ? colors.status.appointment.confirmed
                      : appointment.status === "cancelled"
                      ? colors.status.appointment.cancelled
                      : colors.status.appointment.completed,
                  fontWeight: 600,
                  border:
                    appointment.status === "pending"
                      ? `1px solid ${colors.status.appointment.pending}`
                      : appointment.status === "confirmed"
                      ? `1px solid ${colors.status.appointment.confirmed}`
                      : appointment.status === "cancelled"
                      ? `1px solid ${colors.status.appointment.cancelled}`
                      : `1px solid ${colors.status.appointment.completed}`,
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: "200px" }}>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Datum i vreme
              </Typography>
              <Typography sx={commonStyles.textPrimary}>
                {(() => {
                  const appointmentDate = new Date(appointment.appointmentDate);
                  const dateStr = appointmentDate.toLocaleDateString("sr-RS", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  });
                  const timeStr = appointment.selectedTime || '';
                  return timeStr ? `${dateStr} ${timeStr}` : dateStr;
                })()}
              </Typography>
            </Box>
          </Box>

          {/* Klijent - prikaži samo ako nije customer */}
          {userType !== "customer" && (
            <Box>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Klijent
              </Typography>
              <Typography sx={commonStyles.textPrimary}>
                {appointment.isQuickAppointment && appointment.customerName
                  ? appointment.customerName
                  : appointment.customerId
                  ? typeof appointment.customerId === "object"
                    ? `${appointment.customerId.firstName || ""} ${
                        appointment.customerId.lastName || ""
                      }`.trim() || "N/A"
                    : "N/A"
                  : "N/A"}
              </Typography>
            </Box>
          )}

          {/* Zaposleni */}
          {appointment.employeeId && (
            <Box>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Zaposleni
              </Typography>
              <Typography sx={commonStyles.textPrimary}>
                {typeof appointment.employeeId === "object"
                  ? `${appointment.employeeId.firstName || ""} ${
                      appointment.employeeId.lastName || ""
                    }`.trim() || "N/A"
                  : "N/A"}
              </Typography>
            </Box>
          )}

          {/* Usluge */}
          {appointment.serviceIds && appointment.serviceIds.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Usluge
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {appointment.serviceIds.map((service, idx) => {
                  const serviceObj =
                    typeof service === "object"
                      ? service
                      : services.find((s) => s._id === service);
                  return (
                    <Typography key={idx} sx={commonStyles.textPrimary}>
                      • {serviceObj?.name || "N/A"}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Trajanje i Cena */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: "150px" }}>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Trajanje
              </Typography>
              <Typography sx={commonStyles.textPrimary}>
                {appointment.duration || 0} minuta
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: "150px" }}>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Cena
              </Typography>
              <Typography sx={commonStyles.textPrimary} fontWeight={600}>
                {appointment.totalPrice ? `${appointment.totalPrice} din` : "N/A"}
              </Typography>
            </Box>
          </Box>

          {/* Tenant (samo za super_admin) */}
          {userType === "super_admin" && appointment.tenantId && (
            <Box>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
              >
                Tenant
              </Typography>
              <Typography sx={commonStyles.textPrimary}>
                {typeof appointment.tenantId === "object"
                  ? appointment.tenantId.name || "N/A"
                  : tenants.find((t) => t._id === appointment.tenantId)?.name || "N/A"}
              </Typography>
            </Box>
          )}

          {/* Napomene */}
          {(appointment.notes?.customer || appointment.notes?.employee) && (
            <Box>
              <Typography
                variant="caption"
                sx={{ ...commonStyles.textSecondary, mb: 1, display: "block" }}
              >
                Napomene
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {appointment.notes?.customer && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.03)",
                      border: `1px solid ${customColors.border.light}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
                    >
                      Od klijenta:
                    </Typography>
                    <Typography sx={commonStyles.textPrimary}>
                      {appointment.notes.customer}
                    </Typography>
                  </Box>
                )}
                {appointment.notes?.employee && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: isDarkMode
                        ? "rgba(255, 255, 255, 0.05)"
                        : "rgba(0, 0, 0, 0.03)",
                      border: `1px solid ${customColors.border.light}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ ...commonStyles.textSecondary, mb: 0.5, display: "block" }}
                    >
                      Od zaposlenog:
                    </Typography>
                    <Typography sx={commonStyles.textPrimary}>
                      {appointment.notes.employee}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            ...commonStyles.gradientButton,
            minWidth: "120px",
          }}
        >
          Zatvori
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentViewDialog;

